import { ReactNode, useEffect, useRef, useState } from 'react'
import styled from '@emotion/styled'
import { FusionContentZIndex } from '../../../theme/FusionStyle'

const ARROW_W = 41
const ARROW_H = 25
const ARROW_VIEWBOX = `0 0 ${ARROW_W} ${ARROW_H}`
const ARROW_H_OVERLAP = 6
// move 4px from BobJobberContext top into padding here
// to avoid the shadow being cut off or displayed in the wrong place
// in safari
export const BOB_JOBBER_ARROW_BOTTOM = 4

// to avoid negative values in the math causing console errors
// and possible initial rendering errors
const MIN = 100

const V2L1BobJobberRoot = styled.div({
	position: 'relative',
	display: 'inline-flex',
	zIndex: FusionContentZIndex.Popup,
})

const ContentRoot = styled.div({
	position: 'relative',
	zIndex: FusionContentZIndex.Popup + 2,
	minWidth: `${MIN}px`,
	minHeight: `${MIN}px`,
	display: 'inline-flex',
	flexDirection: 'column',
	rowGap: '1rem',
	padding: `1.5rem 1.5rem calc(1.5rem + ${ARROW_H - ARROW_H_OVERLAP}px + ${BOB_JOBBER_ARROW_BOTTOM}px)`,
	'button, hr': {
		width: '14rem',
	},
	'hr': {
		margin: 0,
	},
})

interface VectorProps {
	jit: boolean
}

const Vector = styled.svg<VectorProps>(({theme, jit}) => ({
	position: 'absolute',
	zIndex: FusionContentZIndex.Popup + 1,
	// this causes a re-render and fixes initial rendering issues in safari
	top: jit ? '0px' : 'auto',
	filter: `drop-shadow(${theme.shadow2})`,
	'rect, path': {
		fill: theme.colors.white,
	},
	'rect': {
		rx: theme.borderRadius,
	},
}))

interface V2L1BobJobberProps {
	children?: ReactNode
}

export const V2L1BobJobber = ({children}: V2L1BobJobberProps) => {
	const rootRef = useRef<HTMLDivElement|null>(null)
	const [viewBox, setViewBox] = useState<[number, number]>([MIN,MIN])
	const [jit, setJit] = useState(false)
	useEffect(() => {
		if(!rootRef.current) {
			return
		}
		const observer = new ResizeObserver(entries => {
			if(entries.length < 1) {
				return
			}
			const { contentRect } = entries[0]
			setViewBox([contentRect.width, contentRect.height])
		})
		observer.observe(rootRef.current)
		return () => observer.disconnect()
	}, [])
	// cause safari to re-render filter properly after layout
	useEffect(() => {
		setJit(false)
		const timeoutId = setTimeout(() => {
			setJit(true)
		}, 0)
		return () => clearTimeout(timeoutId)
	}, [viewBox, setJit])
	const [w, h] = viewBox
	return (
		<V2L1BobJobberRoot ref={rootRef}>
			<Vector viewBox={`0 0 ${w} ${h}`} jit={jit}>
				<rect
					x={0}
					y={0}
					width={w}
					height={h - ARROW_H + ARROW_H_OVERLAP}
				/>
				<svg
					x={(w / 2) - (ARROW_W / 2)}
					y={h - ARROW_H - BOB_JOBBER_ARROW_BOTTOM}
					width={ARROW_W}
					height={ARROW_H}
					viewBox={ARROW_VIEWBOX}
				>
					<path d="M22.6925 23.6525C21.5066 24.9222 19.4934 24.9222 18.3075 23.6524L1.39857 5.54767C-0.392199 3.63026 0.967451 0.5 3.59106 0.5L37.4089 0.500003C40.0325 0.500003 41.3922 3.63026 39.6014 5.54768L22.6925 23.6525Z" />
				</svg>
			</Vector>
			<ContentRoot>
				{children}
			</ContentRoot>
		</V2L1BobJobberRoot>
	)
}
